import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { Faq } from "@/components/Faq";
import { ServiceHero } from "@/components/ServiceHero";
import herrklippningAsset from "@/assets/opt/herrklippning-tailors-barber.webp";
import { Button } from "@/components/ui/button";
import { breadcrumbSchema, faqSchema, jsonLd, absUrl } from "@/lib/seo";
import { GENERIC_BOKADIREKT } from "@/lib/booking";

const BOOK_URL = GENERIC_BOKADIREKT;

const salons = [
  { name: "Täby Centrum", address: "Biblioteksgången 1", hours: "mån–fre 10–20, lör–sön 10–18" },
  { name: "Åhléns City", address: "Klarabergsgatan 50, Stockholm", hours: "mån–fre 10–20, lör 10–19, sön 11–19" },
  { name: "Åkersberga ICA Maxi", address: "Långhundravägen 9", hours: "mån–fre 10–19, lör–sön 10–18" },
  { name: "Arninge ICA Maxi", address: "Kundvägen 1D", hours: "mån–fre 10–19, lör–sön 10–18" },
];

const faq = [
  { q: "Vad kostar seniorklippning?", a: "270 kr för pensionärer och seniorer." },
  { q: "När gäller seniorpriset?", a: "Måndag–Torsdag, klockan 10:00–14:00." },
  { q: "På vilka salonger kan jag boka seniorklippning?", a: "Alla fyra salonger – Täby Centrum, Åhléns City, Åkersberga och Arninge." },
  { q: "Måste jag bevisa att jag är pensionär?", a: "Nej, vi litar på dig." },
  { q: "Kan jag komma på drop-in?", a: "Ja, i mån av tid. Onlinebokning rekommenderas." },
  { q: "Vad ingår i en seniorklippning?", a: "Konsultation, klippning, formning och styling." },
  { q: "Skiljer sig kvaliteten jämfört med en vanlig herrklippning?", a: "Nej. Samma barberare, samma kvalitet." },
];

export const Route = createFileRoute("/seniorklippning-pensionarsklippning")({
  head: () => ({
    meta: [
      { title: "Seniorklippning Stockholm: 270 kr för pensionärer | Tailor's Barber" },
      { name: "description", content: "Seniorklippning för pensionärer hos Tailor's Barber. Klassisk herrklippning för 270 kr måndag till torsdag kl 10 till 14 på fyra salonger. Boka online." },
      { property: "og:title", content: "Seniorklippning Stockholm: 270 kr för pensionärer | Tailor's Barber" },
      { property: "og:description", content: "270 kr, måndag till torsdag kl 10 till 14 på fyra salonger i Stockholm. Boka online." },
      { property: "og:url", content: absUrl("/seniorklippning-pensionarsklippning") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: absUrl("/seniorklippning-pensionarsklippning") }],
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Hem", url: "/" }, { name: "Seniorklippning", url: "/seniorklippning-pensionarsklippning" }])),
      jsonLd(faqSchema(faq)),
    ],
  }),
  component: SeniorPage,
});

function SeniorPage() {
  return (
    <SiteShell>
      <ServiceHero
        breadcrumbName="Seniorklippning"
        eyebrow="Seniorklippning"
        title={<>Seniorklippning för pensionärer – 270 kr</>}
        imageAlt="Seniorklippning Stockholm för pensionärer hos Tailor's Barber"
        intro={
          <>
            Seniorklippning för pensionärer på måndag till torsdag 10 till 14 på alla fyra salonger.
            Klassisk herrklippning med rakade nacke och sidor för 270 kr.
          </>
        }
        priceLabel="270 kr"
        durationLabel="30 min"
        ctaLabel="Boka seniorklippning"
        ctaHref={BOOK_URL}
        image={herrklippningAsset}
        eager
      />


      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">En klassisk och prisvärd klippning för seniorer</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Hos Tailor&apos;s Barber erbjuder vi seniorklippning till ett förmånligt pris för dig som är
          pensionär. Behandlingen utförs av erfarna barberare med fokus på kvalitet, stil och komfort –
          precis som alla våra klippningar.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Pris och tider</h2>
        <ul className="mt-4 space-y-2 text-base md:text-lg text-foreground/85 max-w-3xl">
          <li><strong>Pris:</strong> 270 kr</li>
          <li><strong>Tillgänglig:</strong> Måndag–Torsdag, klockan 10:00–14:00</li>
          <li><strong>Alla fyra salonger:</strong> Täby Centrum, Åhléns City, Åkersberga, Arninge</li>
        </ul>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Vad ingår i en seniorklippning?</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          En seniorklippning hos oss inkluderar konsultation, klippning anpassad efter din hårtyp och
          personliga stil, formning av nacke och sidor samt styling. Du får samma höga service och
          precision som i alla våra övriga behandlingar – till ett pris speciellt anpassat för seniorer.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Boka din seniorklippning</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Onlinebokning via BokaDirekt eller drop-in i mån av tid. Kom gärna tidigt på morgonen för
          kortast möjliga väntetid.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Varför välja Tailor&apos;s Barber för seniorklippning?</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Erfarna barberare med ett lugnt och personligt bemötande. Prisvärd klippning utan att tumma på
          kvaliteten. Centrala lägen med enkel kollektivtrafikförbindelse. Öppet alla dagar i veckan,
          seniorpriset gäller mån–tors 10–14.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Våra salonger</h2>
        <ul className="mt-6 grid gap-3 md:grid-cols-2 max-w-3xl">
          {salons.map((s) => (
            <li key={s.name} className="card-elev p-5">
              <strong>{s.name}</strong>, {s.address}
              <div className="text-sm text-foreground/70 mt-1">Öppet {s.hours}</div>
            </li>
          ))}
        </ul>
      </section>

      <Faq items={faq} heading="Vanliga frågor om seniorklippning" />

      <section className="container-page pb-16">
        <div className="card-elev p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl">Boka din seniorklippning</h2>
          <p className="mt-3 text-foreground/80">270 kr, mån–tors 10–14 på alla fyra salonger.</p>
          <div className="mt-6">
            <Button asChild size="lg">
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka seniorklippning</a>
            </Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
